/**
 * PreviewPane — Step 2 preview components for the CascadeSettingsModal.
 *
 * Contains:
 * - PreviewTwoPaneView: two-pane layout with left nav and right detail pane
 * - PreviewGroupDetail: right pane content for an active setting group
 * - EntityListForSetting: entity list for a single setting, grouped by current value
 * - VirtualEntityRow: virtualized row renderer for the entity list
 */
import { FC, memo, useMemo, useState } from "react"
import {
    colorAttentionBlue10,
    colorAttentionBlue50,
    colorAttentionGreen50,
    colorAttentionRed60,
    colorFunctionalGray10,
    colorFunctionalGray20,
    colorFunctionalGray50,
    colorFunctionalGray70,
    colorFunctionalWhite,
    iconSizeS,
    IconUndo,
    IconX,
    spacingM,
    spacingS,
    spacingSm,
    spacingXs,
} from "@rhumbix/rmbx_design_system_web"
import styled from "styled-components"
import { List, RowComponentProps } from "react-window"
import { ENTITY_ROW_HEIGHT, MAX_ENTITY_LIST_HEIGHT, SETTING_GROUPS, SETTING_LABELS } from "./constants"
import {
    EntityListForSettingProps,
    EntityType,
    PreflightEntity,
    PreviewGroupDetailProps,
    PreviewPaneProps,
    VirtualRow,
    VirtualRowData,
} from "./types"
import { getSettingValueHint } from "./utils"

// ---------------------------------------------------------------------------
// Styled components — Step 2 (preview)
// ---------------------------------------------------------------------------

/** Two-pane layout wrapper shared between Step 1 and Step 2. */
export const TwoPaneContent = styled.div<{ $height?: string }>`
    display: flex;
    gap: ${spacingM};
    height: ${({ $height }) => $height ?? "55vh"};
    padding-top: ${spacingS};
`

/** Left pane for settings toggles (Step 1) and settings navigation (Step 2). */
export const SettingsPane = styled.div`
    width: 260px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: ${spacingXs};
    overflow-y: auto;
    padding-right: ${spacingM};
    border-right: 1px solid ${colorFunctionalGray20};
`

/** Clickable setting name in the Step 2 left nav. */
const SettingNavItem = styled.div<{ $active: boolean }>`
    padding: ${spacingS} ${spacingSm};
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    color: ${({ $active }) => ($active ? colorAttentionBlue50 : colorFunctionalGray70)};
    background: ${({ $active }) => ($active ? colorAttentionBlue10 : "transparent")};
    font-weight: ${({ $active }) => ($active ? 600 : 400)};

    &:hover {
        background: ${({ $active }) => ($active ? colorAttentionBlue10 : colorFunctionalGray10)};
    }
`

/** Right pane container in the Step 2 two-pane layout. */
const PreviewDetailPane = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid ${colorFunctionalGray20};
    border-radius: 4px;
`

/** Sticky header at the top of the Step 2 right pane. */
const PreviewDetailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: ${colorFunctionalGray10};
    border-bottom: 1px solid ${colorFunctionalGray20};
    gap: ${spacingS};
    flex-shrink: 0;
`

const PreviewDetailHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacingS};
    min-width: 0;
    flex: 1;
    overflow: hidden;
`

const PreviewDetailHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacingS};
    flex-shrink: 0;
`

/** Scrollable entity list body in the Step 2 right pane. */
const PreviewDetailBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: none;
    scrollbar-gutter: stable;
    display: flex;
    flex-direction: column;
`

/** Label in the Step 2 right pane header. */
const PreviewDetailLabel = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colorFunctionalGray70};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

/** Collapsible header separating sub-settings within a multi-setting group (e.g. CICO sub-settings). */
const SubSettingHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: ${colorFunctionalGray10};
    border-top: 1px solid ${colorFunctionalGray20};
    border-bottom: 1px solid ${colorFunctionalGray20};
    cursor: pointer;
    user-select: none;

    &:hover {
        background: ${colorFunctionalGray20};
    }
`

/** Left-side group within SubSettingHeader: chevron + label + value hint, kept tightly together. */
const SubSettingHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacingS};
    min-width: 0;
`

const SubSettingLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colorFunctionalGray70};
`

/** "X of Y listed" counter shown in the detail header or sub-setting header. */
const ListedCount = styled.span`
    font-size: 12px;
    color: ${colorAttentionGreen50};
    font-weight: 600;
    white-space: nowrap;
`

/** Exclude All / Include All toggle button. */
const ExcludeAllButton = styled.button`
    flex-shrink: 0;
    border: 1px solid ${colorFunctionalGray20};
    background: ${colorFunctionalWhite};
    border-radius: 4px;
    font-size: 12px;
    color: ${colorFunctionalGray50};
    padding: 3px 8px;
    cursor: pointer;

    &:hover {
        color: ${colorFunctionalGray70};
        border-color: ${colorFunctionalGray50};
    }
`

/**
 * Sticky header within a setting entry body that labels a current-value group.
 * Shows the existing value in red alongside a per-group icon exclude button.
 */
const CurrentValueGroupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 6px 4px 14px;
    background: ${colorFunctionalGray10};
    border-top: 1px solid ${colorFunctionalGray20};
    gap: ${spacingS};
`

const CurrentValueLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colorAttentionRed60};
`

/**
 * Base for icon-only action buttons in the preview pane.
 * Use derived variants (ExcludeButton, RestoreButton, CurrentValueGroupExcludeBtn) rather than this directly.
 */
const IconButton = styled.button<{ $hoverColor: string }>`
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: ${colorFunctionalGray50};
    display: flex;
    align-items: center;
    cursor: pointer;
    justify-content: center;
    padding: 0 2px;

    &:hover {
        color: ${({ $hoverColor }) => $hoverColor};
    }
`

/** Icon-only X button in the current-value group header to exclude all in that group. */
const CurrentValueGroupExcludeBtn = styled(IconButton).attrs({ $hoverColor: colorAttentionRed60 })`
    padding: 0;
`

/** A single project/subgroup row in the setting preview. */
const EntityRow = styled.div<{ $excluded: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 6px 4px 28px;
    border-top: 1px solid ${colorFunctionalGray10};
    opacity: ${({ $excluded }) => ($excluded ? 0.45 : 1)};
    transition: opacity 0.15s ease;

    &:hover {
        background: ${colorFunctionalGray10};
    }
`

const EntityInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`

const EntityName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colorFunctionalGray70};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const EntityParent = styled.span`
    font-size: 11px;
    color: ${colorFunctionalGray50};
`

/** Small thumbnail shown in the detail header when the logo setting is being cascaded. */
const LogoThumbnail = styled.img`
    height: 22px;
    width: auto;
    max-width: 80px;
    object-fit: contain;
    border-radius: 2px;
    margin-left: 6px;
    flex-shrink: 0;
`

/** Muted type label ("Group:" / "Project:") prefixed to entity names in the Step 2 preview list. */
const EntityTypeLabel = styled.span`
    font-size: 11px;
    color: ${colorFunctionalGray50};
    font-weight: 600;
    margin-right: 4px;
    flex-shrink: 0;
`

/** Displays the new value that will be cascaded, shown in the detail header and sub-setting headers. */
const SettingValueDisplay = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colorAttentionGreen50};
    margin-left: 6px;
`

/** Expand/collapse chevron for sub-setting headers. */
const ExpandToggle = styled.span<{ $expanded: boolean }>`
    display: inline-block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 6px;
        height: 6px;
        border-right: 2px solid ${colorFunctionalGray50};
        border-bottom: 2px solid ${colorFunctionalGray50};
        transform: translate(-70%, -50%) rotate(${({ $expanded }) => ($expanded ? "45deg" : "-45deg")});
        transition: transform 0.15s ease;
    }

    &:hover::before {
        border-color: ${colorFunctionalGray70};
    }
`

/** Button to exclude an entity from a setting update. */
const ExcludeButton = styled(IconButton).attrs({ $hoverColor: colorAttentionRed60 })``

/** Button to re-include an excluded entity in a setting update. */
const RestoreButton = styled(IconButton).attrs({ $hoverColor: colorAttentionGreen50 })``

// ---------------------------------------------------------------------------
// Helper: groupEntitiesByCurrentValue
// ---------------------------------------------------------------------------

type CurrentValueGroup = {
    currentValueDisplay: string
    projects: PreflightEntity[]
    subgroups: PreflightEntity[]
}

/**
 * Group projects and subgroups by their current_value_display string.
 * Groups are sorted alphabetically by current value.
 * Within each group, projects and subgroups retain their pre-sort order
 * (callers are expected to sort before passing in).
 *
 * Parameters:
 * projects (PreflightEntity[]): Sorted project list.
 * subgroups (PreflightEntity[]): Sorted subgroup list.
 *
 * Returns:
 * CurrentValueGroup[]: Groups sorted by currentValueDisplay.
 */
function groupEntitiesByCurrentValue(projects: PreflightEntity[], subgroups: PreflightEntity[]): CurrentValueGroup[] {
    const groups = new Map<string, CurrentValueGroup>()
    for (const p of projects) {
        const val = p.current_value_display ?? ""
        if (!groups.has(val)) groups.set(val, { currentValueDisplay: val, projects: [], subgroups: [] })
        groups.get(val)!.projects.push(p)
    }
    for (const s of subgroups) {
        const val = s.current_value_display ?? ""
        if (!groups.has(val)) groups.set(val, { currentValueDisplay: val, projects: [], subgroups: [] })
        groups.get(val)!.subgroups.push(s)
    }
    return [...groups.values()].sort((a, b) => a.currentValueDisplay.localeCompare(b.currentValueDisplay))
}

// ---------------------------------------------------------------------------
// VirtualEntityRow
// ---------------------------------------------------------------------------

/**
 * Row renderer for the virtualized entity list in the Step 2 preview pane.
 * Renders group headers, subgroup rows, and project rows using absolute positioning
 * provided by react-window.
 */
export const VirtualEntityRow: FC<RowComponentProps<VirtualRowData>> = memo(
    ({ index, style, rows, settingKey, excludedProjectIds, excludedSubgroupIds, onExclude, onRestore, onExcludeAll, onRestoreAll }) => {
        const row = rows[index]

        if (row.kind === "currentValueHeader") {
            const allGroupExcluded =
                (row.projects.length + row.subgroups.length) > 0 &&
                row.projects.every(p => excludedProjectIds.has(p.id)) &&
                row.subgroups.every(s => excludedSubgroupIds.has(s.id))
            return (
                <div style={style}>
                    <CurrentValueGroupHeader>
                        <CurrentValueLabel>
                            Current:{" "}
                            {row.display.startsWith("http")
                                ? <LogoThumbnail src={row.display} alt="existing logo" />
                                : row.display}
                        </CurrentValueLabel>
                        <CurrentValueGroupExcludeBtn
                            type="button"
                            title={allGroupExcluded ? "Restore all in this group" : "Exclude all in this group"}
                            onClick={e => {
                                e.stopPropagation()
                                if (allGroupExcluded) {
                                    onRestoreAll(settingKey, "projects", row.projects)
                                    onRestoreAll(settingKey, "subgroups", row.subgroups)
                                } else {
                                    onExcludeAll(settingKey, "projects", row.projects)
                                    onExcludeAll(settingKey, "subgroups", row.subgroups)
                                }
                            }}
                        >
                            {allGroupExcluded
                                ? <IconUndo height={iconSizeS} width={iconSizeS} />
                                : <IconX height={iconSizeS} width={iconSizeS} />
                            }
                        </CurrentValueGroupExcludeBtn>
                    </CurrentValueGroupHeader>
                </div>
            )
        }

        const isProject = row.kind === "project"
        const entityType: EntityType = isProject ? "projects" : "subgroups"
        const isExcluded = isProject ? excludedProjectIds.has(row.entity.id) : excludedSubgroupIds.has(row.entity.id)

        return (
            <div style={style}>
                <EntityRow $excluded={isExcluded} data-testid={isProject ? "preview-project-row" : "preview-subgroup-row"}>
                    <EntityInfo>
                        <EntityName>
                            <EntityTypeLabel>{isProject ? "Project:" : "Group:"}</EntityTypeLabel>
                            {row.entity.name}
                        </EntityName>
                        {isProject && row.entity.parent_name && <EntityParent>{row.entity.parent_name}</EntityParent>}
                    </EntityInfo>
                    {isExcluded ? (
                        <RestoreButton type="button" title="Re-include in update" onClick={() => onRestore(settingKey, entityType, row.entity.id)}>
                            <IconUndo height={iconSizeS} width={iconSizeS} />
                        </RestoreButton>
                    ) : (
                        <ExcludeButton type="button" title="Exclude from update" onClick={() => onExclude(settingKey, entityType, row.entity.id)}>
                            <IconX height={iconSizeS} width={iconSizeS} />
                        </ExcludeButton>
                    )}
                </EntityRow>
            </div>
        )
    }
)

// ---------------------------------------------------------------------------
// EntityListForSetting
// ---------------------------------------------------------------------------

/**
 * Renders the entity list for a single setting key, grouped by current_value_display.
 * Optionally shows a collapsible sub-setting header with the setting label and
 * Exclude/Include All toggle, used for multi-setting groups (e.g. CICO).
 *
 * Parameters:
 * settingKey (CascadeSettingKey): The setting being rendered.
 * entry (PreflightSettingData): Projects and subgroups needing update for this setting.
 * excluded (Excluded): Current per-setting exclusion state.
 * showSubHeader (boolean): Whether to render the collapsible sub-setting header.
 * defaultExpanded (boolean): Whether the entity list starts expanded. Defaults to true.
 * onExclude, onRestore, onExcludeAll, onRestoreAll: Handlers from the parent component.
 */
export const EntityListForSetting: FC<EntityListForSettingProps> = memo(
    ({ settingKey, entry, excluded, showSubHeader, defaultExpanded = true, valueHint, onExclude, onRestore, onExcludeAll, onRestoreAll }) => {
        const [expanded, setExpanded] = useState(defaultExpanded)
        const excEntry = excluded[settingKey]
        const excludedProjectIds = excEntry?.projectIds ?? new Set<number>()
        const excludedSubgroupIds = excEntry?.subgroupIds ?? new Set<number>()

        const sortedProjects = useMemo(() => [...entry.projects].sort((a, b) => a.name.localeCompare(b.name)), [entry.projects])
        const sortedSubgroups = useMemo(() => [...entry.subgroups].sort((a, b) => a.name.localeCompare(b.name)), [entry.subgroups])

        const totalCount = sortedProjects.length + sortedSubgroups.length
        const activeCount =
            sortedProjects.filter(p => !excludedProjectIds.has(p.id)).length +
            sortedSubgroups.filter(s => !excludedSubgroupIds.has(s.id)).length

        const allExcluded = totalCount > 0 && activeCount === 0

        const currentValueGroups = useMemo(
            () => groupEntitiesByCurrentValue(sortedProjects, sortedSubgroups),
            [sortedProjects, sortedSubgroups]
        )

        /** Flatten currentValueGroups into a single list of typed rows for react-window. */
        const flatRows = useMemo<VirtualRow[]>(() => {
            const rows: VirtualRow[] = []
            for (const cvGroup of currentValueGroups) {
                rows.push({ kind: "currentValueHeader", display: cvGroup.currentValueDisplay, projects: cvGroup.projects, subgroups: cvGroup.subgroups })
                for (const entity of cvGroup.subgroups) rows.push({ kind: "subgroup", entity })
                for (const entity of cvGroup.projects) rows.push({ kind: "project", entity })
            }
            return rows
        }, [currentValueGroups])

        const listHeight = Math.min(flatRows.length * ENTITY_ROW_HEIGHT, MAX_ENTITY_LIST_HEIGHT)

        const itemData = useMemo<VirtualRowData>(() => ({
            rows: flatRows,
            settingKey,
            excludedProjectIds,
            excludedSubgroupIds,
            onExclude,
            onRestore,
            onExcludeAll,
            onRestoreAll,
        }), [flatRows, settingKey, excludedProjectIds, excludedSubgroupIds, onExclude, onRestore, onExcludeAll, onRestoreAll])

        return (
            <div
                data-testid="setting-entry-accordion"
                style={showSubHeader ? undefined : { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
            >
                {showSubHeader && (
                    <SubSettingHeader onClick={() => setExpanded(e => !e)}>
                        <SubSettingHeaderLeft>
                            <ExpandToggle $expanded={expanded} />
                            <SubSettingLabel>{SETTING_LABELS[settingKey]}</SubSettingLabel>
                            {valueHint != null && <SettingValueDisplay>{valueHint}</SettingValueDisplay>}
                        </SubSettingHeaderLeft>
                        <PreviewDetailHeaderRight>
                            <ListedCount>
                                Updating {activeCount} of {totalCount}
                            </ListedCount>
                            <ExcludeAllButton
                                type="button"
                                onClick={e => {
                                    e.stopPropagation()
                                    if (allExcluded) {
                                        onRestoreAll(settingKey, "projects", sortedProjects)
                                        onRestoreAll(settingKey, "subgroups", sortedSubgroups)
                                    } else {
                                        onExcludeAll(settingKey, "projects", sortedProjects)
                                        onExcludeAll(settingKey, "subgroups", sortedSubgroups)
                                    }
                                }}
                            >
                                {allExcluded ? "Include All" : "Exclude All"}
                            </ExcludeAllButton>
                        </PreviewDetailHeaderRight>
                    </SubSettingHeader>
                )}
                {(!showSubHeader || expanded) && (
                    <List
                        rowCount={flatRows.length}
                        rowHeight={ENTITY_ROW_HEIGHT}
                        rowComponent={VirtualEntityRow}
                        rowProps={itemData}
                        style={showSubHeader ? { height: listHeight } : { flex: 1, minHeight: 0 }}
                    />
                )}
            </div>
        )
    }
)

// ---------------------------------------------------------------------------
// PreviewGroupDetail
// ---------------------------------------------------------------------------

/**
 * Renders the right pane content for an active setting group.
 *
 * Single-setting groups show a detail header with the group value hint and an
 * Exclude All / Include All toggle, followed by the entity list. Multi-setting
 * groups (CICO) show a group label header, then stacked per-sub-setting sections
 * each with their own header and entity list.
 *
 * Parameters:
 * group (iCompanyGroup): The source group; used to display current values.
 * nameMaps (M2MNameMaps): Name lookups for M2M entity IDs.
 * groupDef (SettingGroupDef): The setting group definition.
 * activeKeys (CascadeSettingKey[]): Setting keys within this group that have updates.
 * preflightSettings (Record<string, PreflightSettingData>): Full preflight settings map.
 * excluded (Excluded): Current per-setting exclusion state.
 * onExclude, onRestore, onExcludeAll, onRestoreAll: Handlers from the parent component.
 */
export const PreviewGroupDetail: FC<PreviewGroupDetailProps> = memo(
    ({ group, nameMaps, groupDef, activeKeys, preflightSettings, excluded, onExclude, onRestore, onExcludeAll, onRestoreAll }) => {
        const isSingleSetting = activeKeys.length === 1
        const singleSettingKey = isSingleSetting ? activeKeys[0] : null
        const singleEntry = singleSettingKey ? preflightSettings[singleSettingKey] : null

        const singleStats = useMemo(() => {
            if (!singleEntry || !singleSettingKey) return null
            const excEntry = excluded[singleSettingKey]
            const total = singleEntry.projects.length + singleEntry.subgroups.length
            const active =
                singleEntry.projects.filter(p => !excEntry?.projectIds?.has(p.id)).length +
                singleEntry.subgroups.filter(s => !excEntry?.subgroupIds?.has(s.id)).length
            return { total, active }
        }, [singleEntry, singleSettingKey, excluded])

        const valueHint = singleSettingKey ? getSettingValueHint(singleSettingKey, group, nameMaps) : null
        const allExcluded = singleStats ? singleStats.active === 0 && singleStats.total > 0 : false

        return (
            <>
                <PreviewDetailHeader>
                    <PreviewDetailHeaderLeft>
                        <PreviewDetailLabel>{groupDef.label}</PreviewDetailLabel>
                        {isSingleSetting && singleSettingKey === "logo" && (
                            group.logo_absolute_url
                                ? <LogoThumbnail src={group.logo_absolute_url} alt="Group logo" />
                                : <SettingValueDisplay>Not set</SettingValueDisplay>
                        )}
                        {isSingleSetting && singleSettingKey !== "logo" && valueHint != null && (
                            <SettingValueDisplay>{valueHint}</SettingValueDisplay>
                        )}
                    </PreviewDetailHeaderLeft>
                    {isSingleSetting && singleStats && singleEntry && (
                        <PreviewDetailHeaderRight>
                            <ListedCount>
                                Updating {singleStats.active} of {singleStats.total}
                            </ListedCount>
                            <ExcludeAllButton
                                type="button"
                                onClick={() => {
                                    if (allExcluded) {
                                        onRestoreAll(singleSettingKey!, "projects", singleEntry.projects)
                                        onRestoreAll(singleSettingKey!, "subgroups", singleEntry.subgroups)
                                    } else {
                                        onExcludeAll(singleSettingKey!, "projects", singleEntry.projects)
                                        onExcludeAll(singleSettingKey!, "subgroups", singleEntry.subgroups)
                                    }
                                }}
                            >
                                {allExcluded ? "Include All" : "Exclude All"}
                            </ExcludeAllButton>
                        </PreviewDetailHeaderRight>
                    )}
                </PreviewDetailHeader>
                <PreviewDetailBody>
                    {activeKeys.map((settingKey, index) => {
                        const entry = preflightSettings[settingKey]
                        if (!entry) return null
                        return (
                            <EntityListForSetting
                                key={settingKey}
                                settingKey={settingKey}
                                entry={entry}
                                excluded={excluded}
                                showSubHeader={!isSingleSetting}
                                defaultExpanded={isSingleSetting || index === 0}
                                valueHint={isSingleSetting ? undefined : getSettingValueHint(settingKey, group, nameMaps)}
                                onExclude={onExclude}
                                onRestore={onRestore}
                                onExcludeAll={onExcludeAll}
                                onRestoreAll={onRestoreAll}
                            />
                        )
                    })}
                </PreviewDetailBody>
            </>
        )
    }
)

// ---------------------------------------------------------------------------
// PreviewTwoPaneView
// ---------------------------------------------------------------------------

/**
 * Two-pane Step 2 layout: left nav lists selected settings, right pane shows
 * the entity detail for the active setting.
 *
 * Parameters:
 * group (iCompanyGroup): The source group.
 * nameMaps (M2MNameMaps): Name lookups for M2M entity IDs.
 * preflightSettings (Record<string, PreflightSettingData>): Full preflight settings map.
 * excluded (Excluded): Current per-setting exclusion state.
 * onExclude, onRestore, onExcludeAll, onRestoreAll: Handlers from the parent component.
 */
const PreviewTwoPaneView: FC<PreviewPaneProps> = memo(({ group, nameMaps, preflightSettings, excluded, onExclude, onRestore, onExcludeAll, onRestoreAll }) => {
    const activeGroups = useMemo(
        () =>
            SETTING_GROUPS.map(g => ({
                groupDef: g,
                activeKeys: g.settingKeys.filter(k => preflightSettings[k]),
            })).filter(({ activeKeys }) => activeKeys.length > 0),
        [preflightSettings]
    )

    const [activeGroupKey, setActiveGroupKey] = useState<string | null>(() => activeGroups[0]?.groupDef.groupKey ?? null)

    const activeGroup = useMemo(
        () => activeGroups.find(({ groupDef }) => groupDef.groupKey === activeGroupKey) ?? null,
        [activeGroups, activeGroupKey]
    )

    return (
        <TwoPaneContent $height="65vh">
            {/* ── Left pane: setting navigation ── */}
            <SettingsPane>
                {activeGroups.map(({ groupDef }) => (
                    <SettingNavItem
                        key={groupDef.groupKey}
                        $active={activeGroupKey === groupDef.groupKey}
                        onClick={() => setActiveGroupKey(groupDef.groupKey)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") setActiveGroupKey(groupDef.groupKey)
                        }}
                    >
                        {groupDef.label}
                    </SettingNavItem>
                ))}
            </SettingsPane>

            {/* ── Right pane: setting detail ── */}
            <PreviewDetailPane>
                {activeGroup && (
                    <PreviewGroupDetail
                        group={group}
                        nameMaps={nameMaps}
                        groupDef={activeGroup.groupDef}
                        activeKeys={activeGroup.activeKeys}
                        preflightSettings={preflightSettings}
                        excluded={excluded}
                        onExclude={onExclude}
                        onRestore={onRestore}
                        onExcludeAll={onExcludeAll}
                        onRestoreAll={onRestoreAll}
                    />
                )}
            </PreviewDetailPane>
        </TwoPaneContent>
    )
})

export default PreviewTwoPaneView
